<template>
  <div v-if="loading || password" :class="isDesktop ? 'w-[564px]' : 'w-full'">
    <div
      v-if="loading"
      role="status"
      aria-busy="true"
      aria-live="polite"
      class="bg-white border border-solid border-slate-400 rounded-sm overflow-hidden box-border"
    >
      <span class="sr-only">読み込み中です</span>
      <!-- 受付番号行 -->
      <div
        data-e2e-id="receiptTableSkeletonRow"
        class="flex items-center bg-table-head border-b border-solid border-slate-400"
      >
        <p
          :class="isDesktop ? LABEL_CLASS_PC : LABEL_CLASS_SP"
          class="font-bold text-ink shrink-0 whitespace-nowrap py-3"
        >
          トラベリスト受付番号
        </p>
        <div
          class="flex-1 min-w-0 flex items-center justify-end gap-2 py-2"
          :class="isDesktop ? VALUE_PX_PC : VALUE_PX_SP"
        >
          <div class="w-[128px] h-5 rounded" :class="bar"></div>
          <div class="w-8 h-8 shrink-0 rounded-lg" :class="bar"></div>
        </div>
      </div>
      <!-- ご連絡先行 -->
      <div
        data-e2e-id="receiptTableSkeletonRow"
        class="flex items-start"
        :class="showStatus ? 'border-b border-solid border-slate-400' : ''"
      >
        <p
          :class="isDesktop ? LABEL_CLASS_PC : LABEL_CLASS_SP"
          class="font-bold text-ink shrink-0 whitespace-nowrap py-4"
        >
          ご連絡先
        </p>
        <div
          class="flex-1 min-w-0 flex justify-end py-4"
          :class="isDesktop ? VALUE_PX_PC : VALUE_PX_SP"
        >
          <div class="w-[176px] h-5 rounded" :class="bar"></div>
        </div>
      </div>
      <!-- ステータス行 -->
      <div
        v-if="showStatus"
        data-e2e-id="receiptTableSkeletonRow"
        class="flex items-start"
      >
        <p
          :class="isDesktop ? LABEL_CLASS_PC : LABEL_CLASS_SP"
          class="font-bold text-ink shrink-0 whitespace-nowrap py-4"
        >
          ステータス
        </p>
        <div
          class="flex-1 min-w-0 flex justify-end py-4"
          :class="isDesktop ? VALUE_PX_PC : VALUE_PX_SP"
        >
          <div class="w-[112px] h-5 rounded" :class="bar"></div>
        </div>
      </div>
    </div>

    <div
      v-else
      class="bg-white border border-solid border-slate-400 rounded-sm overflow-hidden box-border"
    >
      <!-- 受付番号行 -->
      <div
        class="flex items-center bg-table-head border-b border-solid border-slate-400"
      >
        <p
          :class="isDesktop ? LABEL_CLASS_PC : LABEL_CLASS_SP"
          class="font-bold text-ink shrink-0 whitespace-nowrap py-3"
        >
          トラベリスト受付番号
        </p>
        <div
          class="flex-1 min-w-0 flex items-center justify-end gap-2 py-2"
          :class="isDesktop ? VALUE_PX_PC : VALUE_PX_SP"
        >
          <p
            class="shrink-0 text-sm leading-4 font-bold text-ink text-right whitespace-nowrap"
          >
            {{ password }}
          </p>
          <IconButton
            size="base"
            variant="outline-subtle"
            shape="rounded"
            :aria-label="
              copied
                ? 'トラベリスト受付番号をコピーしました'
                : `トラベリスト受付番号 ${password} をコピー`
            "
            data-e2e-id="copyReceiptNumber"
            @click="handleCopyReceiptNumber"
          >
            <i
              :class="copied ? 'fa-check' : 'fa-copy'"
              class="fa-solid text-base text-primary"
              aria-hidden="true"
            ></i>
          </IconButton>
        </div>
      </div>
      <!-- メールアドレス行 -->
      <div
        class="flex items-start"
        :class="showStatus ? 'border-b border-solid border-slate-400' : ''"
      >
        <p
          :class="isDesktop ? LABEL_CLASS_PC : LABEL_CLASS_SP"
          class="font-bold text-ink shrink-0 whitespace-nowrap py-4"
        >
          ご連絡先
        </p>
        <p
          class="flex-1 min-w-0 break-all text-sm leading-4 font-bold text-ink text-right py-4"
          :class="isDesktop ? VALUE_PX_PC : VALUE_PX_SP"
        >
          {{ email }}
        </p>
      </div>
      <!-- ステータス行 -->
      <div
        v-if="showStatus"
        data-e2e-id="receiptTableStatusRow"
        class="flex items-start"
      >
        <p
          :class="isDesktop ? LABEL_CLASS_PC : LABEL_CLASS_SP"
          class="font-bold text-ink shrink-0 whitespace-nowrap py-4"
        >
          ステータス
        </p>
        <p
          class="flex-1 min-w-0 text-sm leading-4 font-bold text-ink text-right py-4"
          :class="isDesktop ? VALUE_PX_PC : VALUE_PX_SP"
        >
          {{ statusText }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useReceiptNumberCopy } from '../../../composables/useReceiptNumberCopy';
import IconButton from '~/src_common/components/parts/button/IconButton.vue';
import { SHIMMER_BAR_CLASS as bar } from '~/src_common/constants/Skeleton';

const props = defineProps({
  password: { type: String, default: '' },
  email: { type: String, default: '' },
  statusText: { type: String, default: '' },
  loading: { type: Boolean, default: false },
  showStatus: { type: Boolean, default: true },
});

const { copied, copy } = useReceiptNumberCopy();

// isDesktop はグローバル mixin（src/main.js=true, src_sp/main.js=false）を
// テンプレートで直接参照する（CompleteStepper.vue と同様のパターン）。
const LABEL_CLASS_PC = 'text-base leading-5 px-4';
const LABEL_CLASS_SP = 'text-sm leading-4 px-3';
const VALUE_PX_PC = 'px-4';
const VALUE_PX_SP = 'px-3';

const handleCopyReceiptNumber = (): Promise<void> => copy(props.password);
</script>
